import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useBranding } from '../hooks/useBranding';

function Addr({ lines }) {
  return <div style={{ fontSize: 13, lineHeight: 1.45 }}>{lines.filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}</div>;
}

export default function WorkOrderPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { ttcLogo } = useBranding();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/api/orders/${id}/work-order`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.error || 'Could not load work order'));
  }, [id]);

  if (err) return <div className="page"><div className="card">{err}</div></div>;
  if (!data) return <div className="page"><div className="loading">Loading…</div></div>;

  const { order, customer, contact, items, billing, charges = [] } = data;
  const chargesTotal = charges.reduce((s2, c) => s2 + Number(c.amount || 0), 0);
  const grandTotal = Number(order.total_amount || 0) + chargesTotal;
  const needsQuote = !!order.needs_quote || (order.items || []).some(it => it.quote_required);
  const money = (n) => (n == null ? '' : `$${Number(n).toFixed(2)}`);
  const contactName = [contact?.first_name, contact?.last_name].filter(Boolean).join(' ');

  return (
    <div className="work-order-wrap" style={{ maxWidth: 820, margin: '0 auto', padding: 24, background: '#fff', color: '#111' }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 14mm; }
          body { background: #fff; }
        }
        .wo-section { border: 1px solid #d5d9e0; border-radius: 8px; margin-bottom: 14px; overflow: hidden; }
        .wo-section > h3 { margin: 0; padding: 8px 12px; background: #031E45; color: #fff; font-size: 13px; letter-spacing: .5px; }
        .wo-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0; }
        .wo-cell { padding: 10px 12px; border-right: 1px solid #eef0f3; }
        .wo-label { font-size: 10px; text-transform: uppercase; letter-spacing: .5px; color: #7a828f; margin-bottom: 3px; }
        .wo-table { width: 100%; border-collapse: collapse; font-size: 12px; }
        .wo-table th, .wo-table td { text-align: left; padding: 6px 12px; border-bottom: 1px solid #eef0f3; vertical-align: top; }
        .wo-attrs { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 16px; font-size: 12px; padding: 8px 12px; }
      `}</style>

      <div className="no-print" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button className="btn btn-secondary btn-sm" onClick={() => nav(`/orders/${id}`)}>← Back to order</button>
        <button className="btn btn-primary btn-sm" onClick={() => window.print()}>Print</button>
        <a className="btn btn-secondary btn-sm" href={`/api/orders/${id}/work-order.pdf`} target="_blank" rel="noopener noreferrer">Download PDF</a>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #031E45', paddingBottom: 12, marginBottom: 16 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {ttcLogo && data.show_logo !== false
              ? <img src={ttcLogo} alt="" style={{ height: 44 }} />
              : <strong style={{ fontSize: 20, color: '#031E45' }}>Toledo Ticket</strong>}
            {data.customer_logo_url && (
              <>
                <span style={{ width: 1, height: 34, background: '#d5d9e0' }} />
                {/* onError hides it if the asset disappears, rather than leaving a broken image on printed paperwork */}
                <img src={data.customer_logo_url} alt={customer?.name || ''} style={{ height: 34 }}
                  onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              </>
            )}
          </div>
          <div style={{ fontSize: 12, color: '#555' }}>Work Order</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#031E45' }}>{order.order_number}</div>
          <div style={{ fontSize: 12 }}>Date: {order.created_at ? order.created_at.slice(0, 10) : ''}</div>
          <div style={{ fontSize: 12 }}>Status: {order.status}</div>
        </div>
      </div>

      <div className="wo-section">
        <div className="wo-grid">
          <div className="wo-cell"><div className="wo-label">Customer</div>{customer.name}</div>
          <div className="wo-cell"><div className="wo-label">{order.po_field_label || 'PO Number'}</div>{order.po_number || '—'}</div>
          <div className="wo-cell" style={{ borderRight: 'none' }}><div className="wo-label">Order Total</div>{money(grandTotal)}</div>
        </div>
      </div>

      <div className="wo-section">
        <div className="wo-grid">
          <div className="wo-cell">
            <div className="wo-label">Ship To</div>
            <Addr lines={[order.ship_to_name, order.ship_to_address, [order.ship_to_city, order.ship_to_state, order.ship_to_zip].filter(Boolean).join(', ')]} />
          </div>
          <div className="wo-cell">
            <div className="wo-label">Bill To {billing.mode !== 'customer' ? `(${billing.mode})` : ''}</div>
            {billing.has
              ? <Addr lines={[billing.name, billing.address1, billing.address2, [billing.city, billing.state, billing.zip].filter(Boolean).join(', '), billing.country, billing.phone]} />
              : <span style={{ color: '#a00', fontSize: 12 }}>No billing address on file</span>}
          </div>
          <div className="wo-cell" style={{ borderRight: 'none' }}>
            <div className="wo-label">Contact</div>
            <Addr lines={[contactName, contact?.email, contact?.phone || 'No phone on file']} />
          </div>
        </div>
      </div>

      {items.map((it, idx) => (
        <div className="wo-section" key={idx}>
          <h3>Item {idx + 1}: {it.product_name} <span style={{ opacity: .8 }}>({it.sku})</span></h3>
          <table className="wo-table">
            <tbody>
              <tr>
                <td><div className="wo-label">Quantity</div>{(it.quantity || 0).toLocaleString()} {it.price_unit === 'per_m' ? 'M' : (it.case_qty_label || 'ea')}</td>
                <td><div className="wo-label">Unit Price</div>{money(it.price_per_unit ?? it.unit_price)}</td>
                <td><div className="wo-label">Line Total</div>{it.quote_required ? 'Toledo Ticket to provide custom pricing' : money(it.line_total)}</td>
                <td><div className="wo-label">Category</div>{it.category || '—'}</td>
              </tr>
            </tbody>
          </table>
          {Object.keys(it.attributes || {}).length > 0 && (
            <>
              <div className="wo-label" style={{ padding: '6px 12px 0' }}>Production specifications</div>
              <div className="wo-attrs">
                {Object.entries(it.attributes).map(([k, v]) => v ? (
                  <div key={k}><strong style={{ textTransform: 'capitalize' }}>{k.replace(/_/g, ' ')}:</strong> {String(v)}</div>
                ) : null)}
              </div>
            </>
          )}
          {it.notes && <div style={{ padding: '4px 12px 10px', fontSize: 12 }}><strong>Notes:</strong> {it.notes}</div>}
        </div>
      ))}

      {charges.length > 0 && (
        <div className="wo-section" style={{ padding: '10px 12px' }}>
          <div className="wo-label" style={{ marginBottom: 6 }}>Additional charges</div>
          {charges.map(c => (
            <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' }}>
              <span>
                {c.label}
                {c.quantity != null && c.unit_rate != null && c.unit !== 'flat'
                  ? ` (${Number(c.quantity).toLocaleString()} × $${Number(c.unit_rate).toFixed(2)})` : ''}
                {c.notes ? ` — ${c.notes}` : ''}
              </span>
              <span>{money(c.amount)}</span>
            </div>
          ))}
          <div style={{ borderTop: '1px solid #d5d9e0', marginTop: 6, paddingTop: 6, fontSize: 13 }}>
            {needsQuote && (
              <div style={{ marginBottom: 6, fontWeight: 600 }}>
                QUOTE REQUIRED — one or more lines are priced by Toledo Ticket. The figures below
                cover the priced lines only.
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Items</span><span>{money(order.total_amount)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Additional charges</span><span>{money(chargesTotal)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}><span>Order total</span><span>{money(grandTotal)}</span></div>
          </div>
        </div>
      )}

      {order.notes && (
        <div className="wo-section"><h3>Order Notes</h3><div style={{ padding: 12, fontSize: 13 }}>{order.notes}</div></div>
      )}
      <div style={{ fontSize: 11, color: '#888', textAlign: 'center', marginTop: 16 }}>
        {order.inside_delivery ? 'Inside delivery • ' : ''}{order.lift_gate ? 'Lift gate • ' : ''}Toledo Ticket Company
      </div>
    </div>
  );
}
